import { BrandDealStatus, BrandTaskStatus, RejectReasonCode } from '../api/payment/brand-deal.api';
import { dealStatusLabel, isValidHttpsUrl } from './brand-deal';
import { parseServerDate } from './server-date';

// Hàm thuần cho "Brief sáng tạo" (M3) — nhãn trạng thái nhiệm vụ, lý do từ chối, đồng hồ đếm hạn, kiểm
// nội dung bài nộp, điều kiện bỏ nhiệm vụ (phía creator, Task 6) + kiểm form tạo brief, nhãn lịch sử
// quyết định (phía doanh nghiệp/admin, Task 7). Tách khỏi component để test không cần dựng React.

export type { BrandTaskStatus, RejectReasonCode };

/** Nhãn tiếng Việt cho trạng thái nhiệm vụ — 6 trạng thái mới tự xử lý, còn lại dùng chung
 * `dealStatusLabel` (M1) để không trùng lặp câu chữ. */
export const taskStatusLabel = (s: BrandTaskStatus): string => {
  switch (s) {
    case 'Working':
      return 'Đang soạn bài';
    case 'Submitted':
      return 'Đã nộp, chờ duyệt';
    case 'RevisionRequested':
      return 'Cần sửa lại';
    case 'Rejected':
      return 'Bị từ chối';
    case 'Abandoned':
      return 'Đã bỏ nhiệm vụ';
    case 'Overdue':
      return 'Đã quá hạn nộp bài';
    case 'Compensated':
      return 'Đã được đền bù';
    default:
      return dealStatusLabel(s as BrandDealStatus);
  }
};

// Danh sách lý do từ chối chuẩn (BrandDealConst.RejectReasonCode phía payment) — Task 7 (màn duyệt bài
// phía doanh nghiệp) dùng lại danh sách này.
export const REJECT_REASONS: Array<{ code: RejectReasonCode; label: string }> = [
  { code: 'OffBrief', label: 'Không đúng brief' },
  { code: 'LowQuality', label: 'Chất lượng chưa đạt' },
  { code: 'NotOriginal', label: 'Nội dung không nguyên bản' },
  { code: 'PolicyViolation', label: 'Vi phạm tiêu chuẩn cộng đồng' },
  { code: 'Other', label: 'Khác' },
];

/** Nhãn của 1 mã lý do từ chối — mã lạ/rỗng trả lại nguyên chuỗi truyền vào (không rõ ⇒ không tự bịa chữ). */
export const rejectReasonLabel = (code: string | null | undefined): string => {
  if (!code) return '';
  return REJECT_REASONS.find((r) => r.code === code)?.label || code;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/** Đồng hồ đếm tới `dueAtIso` nhìn từ `nowMs` — dưới 60 phút hiện phút, từ 1 giờ trở lên hiện giờ (làm
 * tròn lên để không hiện "còn 0 giờ" lúc sắp hết), quá hạn hiện "Đã quá hạn". Dùng cho hạn nộp bài
 * (SubmitDeadline) và hạn doanh nghiệp duyệt (ReviewDueAt). */
export const countdownLabel = (dueAtIso: string | null | undefined, nowMs: number): string => {
  if (!dueAtIso) return '';
  const due = parseServerDate(dueAtIso)?.getTime() ?? NaN;
  if (!Number.isFinite(due)) return '';
  const diffMs = due - nowMs;
  if (diffMs <= 0) return 'Đã quá hạn';
  if (diffMs < HOUR_MS) return `Còn ${Math.ceil(diffMs / MINUTE_MS)} phút`;
  return `Còn ${Math.ceil(diffMs / HOUR_MS)} giờ`;
};

export const BRAND_SUBMISSION_LIMITS = {
  textMaxLength: 2000,
  maxImages: 10,
  maxVideos: 1,
} as const;

export interface DraftContentInput {
  text: string;
  images: number;
  videos: number;
}

/**
 * Kiểm nội dung bài soạn — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi phạm, ĐÚNG
 * thứ tự + ĐÚNG câu chữ với `SubmitTaskHandler`/`BrandSubmissionRules.ValidateContent` phía payment:
 * rỗng (không chữ, không file) ⇒ chữ quá dài ⇒ quá số ảnh/video.
 */
export const validateDraft = (input: DraftContentInput): string | null => {
  const { textMaxLength, maxImages, maxVideos } = BRAND_SUBMISSION_LIMITS;
  const text = input.text?.trim() ?? '';

  if (text.length === 0 && input.images <= 0 && input.videos <= 0) return 'Bài nộp cần có nội dung.';
  if (text.length > textMaxLength) return `Nội dung tối đa ${textMaxLength.toLocaleString('vi-VN')} ký tự.`;
  if (input.images > maxImages || input.videos > maxVideos) return 'Tối đa 10 ảnh và 1 video.';

  return null;
};

export interface AbandonableTask {
  status: BrandTaskStatus;
  latestVersion: number;
}

/** Chỉ bỏ được nhiệm vụ khi còn `Working` VÀ chưa nộp bài lần nào (latestVersion = 0) — khớp
 * `AbandonTaskHandler` phía payment (kiểm đúng trạng thái + chưa có BrandSubmissions nào). */
export const canAbandon = (task: AbandonableTask): boolean =>
  task.status === 'Working' && task.latestVersion === 0;

// ─────────── Doanh nghiệp — tạo brief sáng tạo (Task 7) ───────────

export const BRIEF_FORM_LIMITS = {
  briefTextMinLength: 20,
  briefTextMaxLength: 3000,
  minSubmitDays: 3,
  maxSubmitDays: 14,
  titleMaxLength: 120,
  destinationUrlMaxLength: 500,
  maxImages: 10,
} as const;

export interface BriefFormInput {
  title: string;
  briefText: string;
  submitDays: number;
  destinationUrl?: string;
  images?: number;
  maxRevisions?: number;
}

/**
 * Kiểm form tạo/sửa brief sáng tạo — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi
 * phạm, ĐÚNG thứ tự + ĐÚNG câu chữ với `CreateBrandCampaignHandler`/`UpdateBrandCampaignHandler` nhánh
 * `Kind == Creative` phía payment: tiêu đề (`BrandDealRules.ValidateTitle`) ⇒ mô tả brief + hạn nộp bài
 * (`BrandCreativeRules.ValidateBrief`, 20–3.000 ký tự / 3–14 ngày) ⇒ số ảnh tham khảo ⇒ link đích (tuỳ
 * chọn, chỉ kiểm định dạng nếu có). Giá mỗi suất/số suất/người theo dõi/chủ đề/hạn nhận suất dùng lại
 * NGUYÊN `validateListingForm` (M2, `src/utils/brand-deal.ts`) ở bước "Đăng brief" riêng — không lặp ở đây.
 */
export const validateBriefForm = (input: BriefFormInput): string | null => {
  const { briefTextMinLength, briefTextMaxLength, minSubmitDays, maxSubmitDays, titleMaxLength, destinationUrlMaxLength, maxImages } =
    BRIEF_FORM_LIMITS;

  if (!input.title.trim()) return 'Bạn chưa nhập tiêu đề.';
  if (input.title.length > titleMaxLength) return `Tiêu đề tối đa ${titleMaxLength} ký tự.`;

  const text = input.briefText?.trim() ?? '';
  if (text.length < briefTextMinLength || text.length > briefTextMaxLength) return 'Mô tả brief từ 20 đến 3.000 ký tự.';

  if (!Number.isInteger(input.submitDays) || input.submitDays < minSubmitDays || input.submitDays > maxSubmitDays)
    return 'Hạn nộp bài từ 3 đến 14 ngày.';

  if (
    input.maxRevisions != null &&
    (!Number.isInteger(input.maxRevisions) || input.maxRevisions < 0 || input.maxRevisions > 3)
  )
    return 'Số lần sửa phải từ 0 đến 3.';

  if (input.images != null && input.images > maxImages) return `Tối đa ${maxImages} ảnh.`;

  if (input.destinationUrl && input.destinationUrl.trim()) {
    if (!isValidHttpsUrl(input.destinationUrl)) return 'Link đích phải bắt đầu bằng https://.';
    if (input.destinationUrl.length > destinationUrlMaxLength) return `Link đích tối đa ${destinationUrlMaxLength} ký tự.`;
  }

  return null;
};

// ─────────── Doanh nghiệp/admin — duyệt bài + lịch sử quyết định (Task 7) ───────────

/** Nhãn 1 dòng lịch sử quyết định nhiệm vụ (BrandTaskEventKind phía payment) — timeline màn duyệt bài
 * + nhiệm vụ admin xem. Mã lạ trả lại nguyên chuỗi (không rõ ⇒ không tự bịa chữ). */
export const taskEventLabel = (kind: string): string => {
  switch (kind) {
    case 'Submitted':
      return 'Creator nộp bài';
    case 'RevisionRequested':
      return 'Doanh nghiệp yêu cầu sửa';
    case 'Rejected':
      return 'Doanh nghiệp từ chối';
    case 'Approved':
      return 'Doanh nghiệp duyệt bài';
    case 'BriefUpdated':
      return 'Người đặt cập nhật brief';
    case 'AutoApproved':
      return 'Hệ thống tự duyệt (quá hạn)';
    case 'Overdue':
      return 'Quá hạn nộp bài';
    case 'Abandoned':
      return 'Creator bỏ nhiệm vụ';
    case 'CancelRequested':
      return 'Doanh nghiệp xin huỷ nhiệm vụ';
    case 'CancelApproved':
      return 'Admin đồng ý huỷ nhiệm vụ';
    case 'CancelRejected':
      return 'Admin không đồng ý huỷ nhiệm vụ';
    case 'PublishFailed':
      return 'Đăng bài lỗi';
    case 'AdminPaid':
      return 'Admin trả tiền creator';
    case 'AdminRefunded':
      return 'Admin hoàn tiền doanh nghiệp';
    // M4 — kháng cáo bản quyền (spec 2026-10-05-brand-dispute).
    case 'DisputeOpened':
      return 'Creator kháng cáo';
    case 'DisputeResponded':
      return 'Doanh nghiệp giải trình';
    case 'DisputeUpheld':
      return 'Admin xác nhận vi phạm';
    case 'DisputeDismissed':
      return 'Admin bác kháng cáo (không đủ căn cứ)';
    case 'SlotReleased':
      return 'Hệ thống trả suất';
    // Đơn nhận đề xuất (spec 2026-10-12 §6).
    case 'CreatorPublished':
      return 'Creator bấm Đăng công khai';
    case 'AutoPublished':
      return 'Hệ thống tự đăng do quá hạn';
    default:
      return kind;
  }
};

export const CANCEL_REQUEST_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;
export const REVISION_NOTE_LIMITS = { minLength: 5, maxLength: 500 } as const;
export const REJECT_REASON_TEXT_MAX_LENGTH = 200;

/** Khung 48 giờ doanh nghiệp duyệt bài — chỉ hiện khi deal `Submitted` và có `reviewDueAt`. */
export const REVIEW_WINDOW_HOURS = 48;

/** Dòng dấu chìm phủ lên phần CHỮ của bài nộp — ĐÚNG câu chữ với `BrandWatermark.Text` phía payment
 * (ảnh/video đã có dấu đóng thẳng vào file; phần chữ không đóng được nên FE tự phủ lớp CSS này đè
 * lên). `dealId` dạng chuẩn có gạch nối (API trả về) — bỏ gạch nối trước khi lấy 6 ký tự đầu, khớp
 * `Guid.ToString("N")` phía payment. */
export const draftWatermarkText = (brandName: string, dealId: string): string => {
  const shortId = dealId.replace(/-/g, '').slice(0, 6).toUpperCase();
  return `Bản nháp · ${brandName} · ${shortId}`;
};

// ─────────── Luật đơn brief sáng tạo (spec 2026-10-10-brand-order-rules §2, §4) ───────────

/** Số lần người đặt được yêu cầu sửa, chọn lúc tạo brief (payment `BrandDealConst.Min/MaxMaxRevisions`). */
export const MAX_REVISION_OPTIONS = [0, 1, 2, 3] as const;
export const DEFAULT_MAX_REVISIONS = 2;

/** Số ngày duyệt đọc từ cài đặt công khai `brandCreative.reviewDays`, kẹp như payment `BrandCreativeRules.ReviewDaysFrom`. */
export const REVIEW_DAYS_LIMITS = { min: 3, max: 5, fallback: 3 } as const;
export const REVIEW_DAYS_SETTING_KEY = 'brandCreative.reviewDays';

/** Brief tạo trước khi có luật đơn (reviewDays null) vẫn duyệt trong 48 giờ, lời văn gọi là 2 ngày (payment `LegacyReviewDisplayDays`). */
export const LEGACY_REVIEW_DISPLAY_DAYS = 2;

/** Chú thích ô số lần sửa bị khoá — ĐÚNG câu lỗi payment `BrandDealConst.MaxRevisionsLockedMessage`. */
export const MAX_REVISIONS_LOCKED_HINT = 'Brief đã có người nhận nên không đổi được số lần sửa.';

export const clampReviewDays = (raw: string | number | null | undefined): number => {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n)) return REVIEW_DAYS_LIMITS.fallback;
  return Math.min(REVIEW_DAYS_LIMITS.max, Math.max(REVIEW_DAYS_LIMITS.min, Math.trunc(n)));
};

export const displayReviewDays = (reviewDays: number | null | undefined): number => reviewDays ?? LEGACY_REVIEW_DISPLAY_DAYS;

/** Nhãn luật đơn ở thẻ brief, "Việc của tôi" và bàn soạn bài (spec §2.4). Máy chủ cũ chưa trả số lần sửa ⇒ 2. */
export const creativeRulesLabel = (maxRevisions: number | null | undefined, reviewDays: number | null | undefined): string => {
  const n = maxRevisions ?? DEFAULT_MAX_REVISIONS;
  const d = displayReviewDays(reviewDays);
  return n > 0 ? `Sửa tối đa ${n} lần · Duyệt trong ${d} ngày` : `Không có lượt sửa · Duyệt trong ${d} ngày`;
};

/** Dòng dưới ô số lần sửa ở form brief (spec §4). */
export const reviewWindowHint = (days: number): string => `Bạn có ${days} ngày để duyệt mỗi lần creator nộp`;

export interface RevisionUsage {
  revisionCount: number;
  maxRevisions?: number | null;
}

/** Khớp payment `RequestRevisionHandler`: chặn khi revisionCount ≥ maxRevisions (kể cả 0). */
export const canRequestRevision = (t: RevisionUsage): boolean => t.revisionCount < (t.maxRevisions ?? DEFAULT_MAX_REVISIONS);

/** Dòng thay nút "Yêu cầu sửa" khi hết lượt. Brief 0 lượt sửa nói rõ thay vì "hết 0 lần" (ruling R11). */
export const revisionExhaustedLabel = (t: RevisionUsage): string => {
  const n = t.maxRevisions ?? DEFAULT_MAX_REVISIONS;
  return n > 0 ? `Đã dùng hết ${n} lần sửa` : 'Brief này không có lượt sửa';
};

export const revisionCountLabel = (t: RevisionUsage): string => `${t.revisionCount}/${t.maxRevisions ?? DEFAULT_MAX_REVISIONS}`;
